import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChangeEvent, DragEvent, useMemo, useState } from 'react';
import { BASE_API_PATH } from '../constants';
import { isProd } from '../utils';

type BlogCategory = 'Reflections' | 'Life' | 'Projects';
type BodyBlockType = 'paragraph' | 'image';

interface BodyBlock {
    localId: string;
    type: BodyBlockType;
    text: string;
    imageFile: File | null;
    imagePreviewUrl: string;
}

interface UploadImagePayload {
    fileName: string;
    contentType: string;
    base64Data: string;
}

interface TextComponent {
    type: 'string' | 'link';
    text: string;
    link?: string;
}

interface ParagraphSegment {
    type: 'paragraph' | 'unordered_list' | 'ordered_list';
    text?: string;
    items?: string[];
}

const BLOG_CATEGORIES: BlogCategory[] = ['Reflections', 'Life', 'Projects'];

function parseInlineText(input: string): TextComponent[] {
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const result: TextComponent[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(input)) !== null) {
        if (match.index > lastIndex) {
            result.push({ type: 'string', text: input.substring(lastIndex, match.index) });
        }

        const label = match[1].trim();
        const link = match[2].trim();
        result.push({ type: 'link', text: label, link });
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < input.length) {
        result.push({ type: 'string', text: input.substring(lastIndex) });
    }

    return result;
}

function renderPreviewInlineText(text: string) {
    return parseInlineText(text).map((component, index) => {
        if (component.type === 'string') {
            return <Box component="span" key={`preview-text-${index}`}>{component.text}</Box>;
        }

        return (
            <Link
                key={`preview-link-${index}`}
                href={component.link}
                underline="always"
                color="#1d4f9e"
                target="_blank"
                rel="noopener"
                sx={{
                    fontWeight: 600,
                    textUnderlineOffset: '2px',
                    textDecorationThickness: '2px',
                    '&:hover': {
                        color: '#163f86',
                        textDecorationThickness: '3px',
                    },
                    '&:focus-visible': {
                        outline: '2px solid #7da8e8',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                    },
                }}
            >
                {component.text}
            </Link>
        );
    });
}

function parseParagraphSegments(input: string): ParagraphSegment[] {
    const segments: ParagraphSegment[] = [];
    const paragraphLines: string[] = [];
    let listType: ParagraphSegment['type'] | null = null;
    let listItems: string[] = [];

    const flushParagraph = () => {
        const paragraphText = paragraphLines.join('\n').trim();
        if (paragraphText) {
            segments.push({ type: 'paragraph', text: paragraphText });
        }
        paragraphLines.length = 0;
    };

    const flushList = () => {
        if (listType && listItems.length > 0) {
            segments.push({ type: listType, items: [...listItems] });
        }
        listType = null;
        listItems = [];
    };

    for (const rawLine of input.split('\n')) {
        const trimmed = rawLine.trim();
        if (!trimmed) {
            flushParagraph();
            flushList();
            continue;
        }

        const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
        const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
        if (unorderedMatch || orderedMatch) {
            flushParagraph();
            const nextListType: ParagraphSegment['type'] = unorderedMatch ? 'unordered_list' : 'ordered_list';
            const itemText = (unorderedMatch?.[1] || orderedMatch?.[1] || '').trim();
            if (listType && listType !== nextListType) {
                flushList();
            }
            listType = nextListType;
            listItems.push(itemText);
            continue;
        }

        flushList();
        paragraphLines.push(rawLine);
    }

    flushParagraph();
    flushList();
    return segments;
}

function toSlug(input: string) {
    return input
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function createBlock(type: BodyBlockType): BodyBlock {
    return {
        localId: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${type}-${Date.now()}-${Math.random()}`,
        type,
        text: '',
        imageFile: null,
        imagePreviewUrl: '',
    };
}

function toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function BlogPostBuilder() {
    const [title, setTitle] = useState('');
    const [id, setId] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<BlogCategory>('Life');
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverFilePreviewUrl, setCoverFilePreviewUrl] = useState('');
    const [timestamp, setTimestamp] = useState(() => `${Math.floor(Date.now() / 1000)}`);
    const [includeInFeed, setIncludeInFeed] = useState(true);
    const [bodyBlocks, setBodyBlocks] = useState<BodyBlock[]>([createBlock('paragraph')]);
    const [showPreview, setShowPreview] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [resultMessage, setResultMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null);
    const [dragOverBlockId, setDragOverBlockId] = useState<string | null>(null);

    const resolvedId = useMemo(() => {
        if (id.trim()) {
            return toSlug(id);
        }
        return toSlug(`${category}-${title}`);
    }, [category, id, title]);

    const coverPreviewUrl = useMemo(() => coverFilePreviewUrl, [coverFilePreviewUrl]);
    const parsedPreviewTimestamp = useMemo(() => Number.parseInt(timestamp, 10), [timestamp]);
    const previewDateLabel = useMemo(() => {
        if (!Number.isInteger(parsedPreviewTimestamp)) {
            return 'Invalid date';
        }
        return new Date(parsedPreviewTimestamp * 1000).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }, [parsedPreviewTimestamp]);
    const previewBlocks = useMemo(
        () => bodyBlocks.filter((block) => block.text.trim() || block.imagePreviewUrl),
        [bodyBlocks],
    );

    const handleBlockChange = (blockId: string, updates: Partial<BodyBlock>) => {
        setBodyBlocks((previous) =>
            previous.map((block) => (block.localId === blockId ? { ...block, ...updates } : block)),
        );
    };

    const handleRemoveBlock = (blockId: string) => {
        setBodyBlocks((previous) => previous.filter((block) => block.localId !== blockId));
    };

    const handleAddBlock = (type: BodyBlockType) => {
        setBodyBlocks((previous) => [...previous, createBlock(type)]);
    };

    const handleDragStart = (blockId: string, event: DragEvent<HTMLElement>) => {
        setDraggedBlockId(blockId);
        setDragOverBlockId(blockId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', blockId);
    };

    const handleDragEnter = (blockId: string) => {
        if (!draggedBlockId || blockId === draggedBlockId) {
            return;
        }
        setDragOverBlockId(blockId);
    };

    const handleDragEnd = () => {
        setDraggedBlockId(null);
        setDragOverBlockId(null);
    };

    const handleDrop = (targetBlockId: string) => {
        if (!draggedBlockId || draggedBlockId === targetBlockId) {
            handleDragEnd();
            return;
        }
        setBodyBlocks((previous) => {
            const reordered = [...previous];
            const sourceIndex = reordered.findIndex((block) => block.localId === draggedBlockId);
            const targetIndex = reordered.findIndex((block) => block.localId === targetBlockId);
            if (sourceIndex < 0 || targetIndex < 0) {
                return previous;
            }
            const [moved] = reordered.splice(sourceIndex, 1);
            reordered.splice(targetIndex, 0, moved);
            return reordered;
        });
        handleDragEnd();
    };

    const handleCoverFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] || null;
        setCoverFile(selectedFile);
        if (selectedFile) {
            setCoverFilePreviewUrl(URL.createObjectURL(selectedFile));
        } else {
            setCoverFilePreviewUrl('');
        }
    };

    const handleBodyImageFileSelection = (blockId: string, event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] || null;
        handleBlockChange(blockId, {
            imageFile: selectedFile,
            imagePreviewUrl: selectedFile ? URL.createObjectURL(selectedFile) : '',
        });
    };

    const handleSubmit = async () => {
        setResultMessage('');
        setErrorMessage('');

        if (!title.trim()) {
            setErrorMessage('Title is required.');
            return;
        }
        if (!resolvedId) {
            setErrorMessage('Could not infer a valid ID. Provide one explicitly.');
            return;
        }
        if (!description.trim()) {
            setErrorMessage('Description is required.');
            return;
        }
        if (!coverFile) {
            setErrorMessage('Cover image upload is required.');
            return;
        }

        const parsedTimestamp = Number.parseInt(timestamp, 10);
        if (!Number.isInteger(parsedTimestamp)) {
            setErrorMessage('Timestamp must be a valid unix timestamp.');
            return;
        }

        const normalizedBody = bodyBlocks
            .map((block) => ({
                type: block.type,
                text: block.text.trim(),
                imageFile: block.imageFile,
            }))
            .filter((block) => block.text || block.imageFile);

        if (normalizedBody.length === 0) {
            setErrorMessage('Add at least one body block.');
            return;
        }

        for (const block of normalizedBody) {
            if (block.type === 'image' && !block.imageFile) {
                setErrorMessage('Image blocks require an uploaded file.');
                return;
            }
        }

        setSubmitting(true);
        try {
            let coverImagePayload: UploadImagePayload | undefined;
            if (coverFile) {
                coverImagePayload = {
                    fileName: coverFile.name,
                    contentType: coverFile.type || 'image/jpeg',
                    base64Data: await toBase64(coverFile),
                };
            }
            const bodyPayload = await Promise.all(
                normalizedBody.map(async (block) => {
                    if (block.type !== 'image') {
                        return { type: 'paragraph', text: block.text };
                    }
                    if (block.imageFile) {
                        return {
                            type: 'image',
                            text: block.text,
                            imageUpload: {
                                fileName: block.imageFile.name,
                                contentType: block.imageFile.type || 'image/jpeg',
                                base64Data: await toBase64(block.imageFile),
                            },
                        };
                    }
                    return {
                        type: 'image',
                        text: block.text,
                    };
                }),
            );
            const response = await fetch(`${BASE_API_PATH}/blog`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'blog',
                    timestamp: parsedTimestamp,
                    id: resolvedId,
                    title: title.trim(),
                    description: description.trim(),
                    category,
                    coverImage: coverImagePayload,
                    includeInFeed,
                    body: bodyPayload,
                }),
            });

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(errorBody || 'Could not submit blog post.');
            }

            setResultMessage(`Created blog post '${resolvedId}'.`);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Could not submit blog post.');
        } finally {
            setSubmitting(false);
        }
    };

    if (isProd()) {
        return (
            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Typography sx={{ color: '#55729f' }}>Builder is only available in beta/local environments.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>
                Blog Post Builder
            </Typography>
            <Typography sx={{ color: '#55729f', mb: 2.5 }}>
                Dev-only uploader that creates blog entries in beta/local.
            </Typography>

            <Stack spacing={1.5}>
                <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
                <TextField
                    label="Post ID (optional slug)"
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                    helperText={resolvedId ? `Resolved ID: ${resolvedId}` : undefined}
                />
                <TextField
                    label="Description"
                    multiline
                    minRows={2}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />
                <TextField select label="Category" value={category} onChange={(event) => setCategory(event.target.value as BlogCategory)}>
                    {BLOG_CATEGORIES.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateOutlinedIcon />}>
                    Upload cover image
                    <input type="file" hidden accept="image/*" onChange={handleCoverFileSelection} />
                </Button>
                {coverFile && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>Selected cover: {coverFile.name}</Typography>
                )}
                {coverPreviewUrl && (
                    <Box sx={{ width: 220 }}>
                        <Typography sx={{ color: '#55729f', fontSize: 12, mb: 0.5 }}>Cover preview</Typography>
                        <Box
                            component="img"
                            src={coverPreviewUrl}
                            alt="Cover preview"
                            sx={{ width: '100%', borderRadius: 1.5, border: '1px solid #d9e7f8', aspectRatio: '16 / 10', objectFit: 'cover' }}
                        />
                    </Box>
                )}
                <TextField
                    label="Published timestamp (unix seconds)"
                    value={timestamp}
                    onChange={(event) => setTimestamp(event.target.value)}
                />
                <FormControlLabel
                    control={<Switch checked={includeInFeed} onChange={(event) => setIncludeInFeed(event.target.checked)} />}
                    label="Add post to feed"
                />

                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>Body blocks</Typography>
                    {bodyBlocks.length > 1 && (
                        <Typography sx={{ color: '#55729f', fontSize: 13, mb: 1 }}>
                            Drag and drop blocks to set body order.
                        </Typography>
                    )}
                    <Stack spacing={1.2}>
                        {bodyBlocks.map((block, index) => (
                            <Box
                                key={block.localId}
                                onDragEnter={() => handleDragEnter(block.localId)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={() => handleDrop(block.localId)}
                                onDragEnd={handleDragEnd}
                                sx={{
                                    border: '1px solid #d9e7f8',
                                    borderRadius: 2,
                                    p: 1.2,
                                    transition: 'border-color 120ms ease, background-color 120ms ease',
                                    borderColor: dragOverBlockId === block.localId ? '#7da8e8' : '#d9e7f8',
                                    backgroundColor: dragOverBlockId === block.localId ? '#f1f7ff' : 'transparent',
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                        <Box
                                            draggable
                                            onDragStart={(event) => handleDragStart(block.localId, event)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#55729f',
                                                cursor: 'grab',
                                            }}
                                        >
                                            <DragIndicatorIcon fontSize="small" />
                                        </Box>
                                        <Typography sx={{ color: '#1f4f9c', fontWeight: 700, fontSize: 13 }}>
                                            #{index + 1} {block.type === 'image' ? 'Image block' : 'Paragraph block'}
                                        </Typography>
                                    </Stack>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveBlock(block.localId)}
                                        disabled={bodyBlocks.length === 1}
                                    >
                                        x
                                    </IconButton>
                                </Stack>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={block.type === 'image' ? 2 : 4}
                                    label={block.type === 'image' ? 'Caption / supporting text' : 'Paragraph text'}
                                    value={block.text}
                                    onChange={(event) => handleBlockChange(block.localId, { text: event.target.value })}
                                />
                                {block.type === 'image' && (
                                    <>
                                        <Button
                                            sx={{ mt: 1 }}
                                            variant="outlined"
                                            component="label"
                                            startIcon={<AddPhotoAlternateOutlinedIcon />}
                                        >
                                            Upload body image
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(event) => handleBodyImageFileSelection(block.localId, event)}
                                            />
                                        </Button>
                                        {block.imageFile && (
                                            <Typography sx={{ color: '#55729f', fontSize: 13, mt: 1 }}>
                                                Selected image: {block.imageFile.name}
                                            </Typography>
                                        )}
                                        {block.imagePreviewUrl && (
                                            <Box sx={{ mt: 1, width: 220 }}>
                                                <Typography sx={{ color: '#55729f', fontSize: 12, mb: 0.5 }}>Image preview</Typography>
                                                <Box
                                                    component="img"
                                                    src={block.imagePreviewUrl}
                                                    alt="Body image preview"
                                                    sx={{
                                                        width: '100%',
                                                        borderRadius: 1.5,
                                                        border: '1px solid #d9e7f8',
                                                        aspectRatio: '16 / 10',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" onClick={() => handleAddBlock('paragraph')}>
                            Add paragraph
                        </Button>
                        <Button variant="outlined" startIcon={<AddPhotoAlternateOutlinedIcon />} onClick={() => handleAddBlock('image')}>
                            Add image block
                        </Button>
                    </Stack>
                </Box>

                <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Submitting...' : 'Create blog post in beta'}
                </Button>
                <Button variant="outlined" onClick={() => setShowPreview((previous) => !previous)}>
                    {showPreview ? 'Hide preview' : 'Preview draft'}
                </Button>
            </Stack>

            {showPreview && (
                <Box
                    sx={{
                        mt: 3,
                        borderRadius: 3,
                        border: '1px solid #d9e7f8',
                        backgroundColor: '#f8fbff',
                        p: { xs: 2, md: 3 },
                    }}
                >
                    <Typography sx={{ color: '#4d72a9', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        {category}
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#163f86', mt: 0.4 }}>
                        {title.trim() || 'Untitled post'}
                    </Typography>
                    <Typography sx={{ color: '#55729f', fontSize: 17, mt: 0.5 }}>{previewDateLabel}</Typography>

                    {coverPreviewUrl && (
                        <Box sx={{ mt: 2, mb: 2.5 }}>
                            <Box
                                component="img"
                                src={coverPreviewUrl}
                                alt="Cover preview"
                                sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    border: '1px solid #d9e7f8',
                                    aspectRatio: '16 / 10',
                                    objectFit: 'cover',
                                }}
                            />
                        </Box>
                    )}

                    {previewBlocks.length === 0 ? (
                        <Typography sx={{ color: '#55729f', fontStyle: 'italic' }}>
                            Add paragraph/image blocks to preview the post body.
                        </Typography>
                    ) : (
                        <Stack spacing={2.5}>
                            {previewBlocks.map((block) =>
                                block.type === 'image' ? (
                                    <Box key={`${block.localId}-preview`}>
                                        {block.imagePreviewUrl ? (
                                            <Box
                                                component="img"
                                                src={block.imagePreviewUrl}
                                                alt="Body block preview"
                                                sx={{
                                                    width: '100%',
                                                    borderRadius: 2,
                                                    border: '1px solid #d9e7f8',
                                                    aspectRatio: '16 / 10',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : (
                                            <Typography sx={{ color: '#55729f', fontStyle: 'italic' }}>
                                                Upload an image for this block.
                                            </Typography>
                                        )}
                                        {block.text.trim() && (
                                            <Typography sx={{ color: '#55729f', fontStyle: 'italic', mt: 0.75 }}>
                                                {block.text.trim()}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography
                                        key={`${block.localId}-preview`}
                                        component="div"
                                        sx={{ color: '#2f5487', fontSize: 19, lineHeight: 1.55 }}
                                    >
                                        {parseParagraphSegments(block.text.trim()).map((segment, segmentIndex) => {
                                            if (segment.type === 'paragraph') {
                                                return (
                                                    <Box
                                                        key={`preview-paragraph-${segmentIndex}`}
                                                        component="p"
                                                        sx={{ margin: 0, marginBottom: 2, whiteSpace: 'pre-line' }}
                                                    >
                                                        {renderPreviewInlineText(segment.text || '')}
                                                    </Box>
                                                );
                                            }

                                            const listItems = segment.items || [];
                                            return (
                                                <Box
                                                    key={`preview-list-${segment.type}-${segmentIndex}`}
                                                    component={segment.type === 'ordered_list' ? 'ol' : 'ul'}
                                                    sx={{ margin: 0, marginBottom: 2, paddingLeft: '1.5em' }}
                                                >
                                                    {listItems.map((item, itemIndex) => (
                                                        <Box
                                                            key={`preview-list-item-${itemIndex}`}
                                                            component="li"
                                                            sx={{ marginBottom: 0.5 }}
                                                        >
                                                            {renderPreviewInlineText(item)}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            );
                                        })}
                                    </Typography>
                                ),
                            )}
                        </Stack>
                    )}
                </Box>
            )}

            {resultMessage && <Typography sx={{ color: '#2d7a3e', mt: 2 }}>{resultMessage}</Typography>}
            {errorMessage && <Typography sx={{ color: '#b3261e', mt: 2 }}>{errorMessage}</Typography>}
        </Box>
    );
}
